import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axiosInstance from '../../utils/axiosInstance';

const STATUS_LABELS = {
  open: 'Open',
  'in-progress': 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All', active: 'bg-slate-800 text-white' },
  { value: 'open', label: 'Open', active: 'bg-amber-500 text-white' },
  { value: 'in-progress', label: 'In Progress', active: 'bg-sky-500 text-white' },
  { value: 'resolved', label: 'Resolved', active: 'bg-emerald-500 text-white' },
  { value: 'closed', label: 'Closed', active: 'bg-slate-500 text-white' },
];

function fmtDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoStr(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function courseBaseName(course) {
  return course?.courseShortName || course?.shortName || course?.courseName || course?.fullName || 'Course';
}

function courseLabel(course) {
  const name = courseBaseName(course);
  const schedule = course?.scheduleType ? course.scheduleType.charAt(0).toUpperCase() + course.scheduleType.slice(1) : null;
  return schedule ? `${name} (${schedule})` : name;
}

export default function TicketsReport() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);

  const [selectedCourseIds, setSelectedCourseIds] = useState(['all']);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const courseDropdownRef = useRef(null);
  const [categoryId, setCategoryId] = useState('all');
  const [status, setStatus] = useState('all');
  const [startDate, setStartDate] = useState(daysAgoStr(30));
  const [endDate, setEndDate] = useState(todayStr());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce the free-text search so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    axiosInstance.get('/admin/me/coordinate-courses').then(({ data }) => {
      const list = Object.values(data?.coordinateCourses || {});
      setCourses(list);
    }).catch(() => {});
    axiosInstance.get('/tickets/categories').then(({ data }) => {
      setCategories(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  // Closes the course dropdown when clicking anywhere outside it.
  useEffect(() => {
    if (!courseDropdownOpen) return undefined;
    const handleClick = (e) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(e.target)) {
        setCourseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [courseDropdownOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const courseIds = selectedCourseIds.includes('all') ? 'all' : selectedCourseIds.join(',');
        const { data } = await axiosInstance.get('/tickets/report', {
          params: { courseIds, categoryId, status, startDate, endDate, search: search || undefined },
        });
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load the report.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCourseIds, categoryId, status, startDate, endDate, search]);

  const courseName = useMemo(() => {
    if (selectedCourseIds.includes('all') || selectedCourseIds.length === 0) return 'All Courses';
    if (selectedCourseIds.length === 1) {
      const match = courses.find((c) => String(c.courseId) === selectedCourseIds[0]);
      return match ? courseBaseName(match) : 'Course';
    }
    return `${selectedCourseIds.length} courses selected`;
  }, [selectedCourseIds, courses]);

  const downloadExcel = () => {
    const headers = ['Ticket ID', 'Title', 'Status', 'Priority', 'Course', 'Batch', 'Student Name', 'Student ID', 'Registration No', 'Category', 'Created At', 'Resolved At', 'Resolved By'];
    const data = rows.map((r) => [
      r.ticketId,
      r.title,
      STATUS_LABELS[r.status] || r.status,
      r.priority,
      r.courseName || '',
      r.batchName || '',
      r.studentName || '',
      r.studentId ?? '',
      r.registrationNumber || '',
      r.categoryName || '',
      r.createdAt ? fmtDateTime(r.createdAt) : '',
      r.resolvedAt ? fmtDateTime(r.resolvedAt) : '',
      r.resolvedByName || '',
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    worksheet['!cols'] = headers.map(() => ({ wch: 18 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tickets Report');
    XLSX.writeFile(workbook, `Tickets_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/admin/dashboard'))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Tickets Report</h1>
            <p className="text-xs text-slate-500">Filter by course, category, date range, and student — then export to Excel</p>
          </div>
        </div>

        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <div className="relative" ref={courseDropdownRef}>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">Course</label>
              <button
                type="button"
                onClick={() => setCourseDropdownOpen((v) => !v)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-sm text-slate-700 outline-none focus:border-violet-400"
              >
                <span className="truncate">{courseName}</span>
                <svg className="h-4 w-4 shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>

              {courseDropdownOpen && (
                <div className="absolute left-0 top-full z-20 mt-1 w-72 rounded-lg border border-slate-200 bg-white shadow-lg">
                  <div className="border-b border-slate-100 p-2">
                    <input
                      type="text"
                      value={courseSearchQuery}
                      onChange={(e) => setCourseSearchQuery(e.target.value)}
                      placeholder="Search courses…"
                      className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 outline-none focus:border-violet-400"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {(() => {
                      const visible = courses.filter((c) => courseLabel(c).toLowerCase().includes(courseSearchQuery.trim().toLowerCase()));
                      const visibleIds = visible.map((c) => String(c.courseId));
                      const allChecked = visibleIds.length > 0 && (selectedCourseIds.includes('all') || visibleIds.every((id) => selectedCourseIds.includes(id)));
                      return (
                        <>
                          <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={allChecked}
                              onChange={(e) => setSelectedCourseIds(e.target.checked ? ['all'] : [])}
                              className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                            />
                            <span className="font-semibold text-slate-700">Select All</span>
                          </label>
                          <div className="my-1 border-t border-slate-100" />
                          {visible.length === 0 ? (
                            <p className="px-2 py-3 text-center text-xs text-slate-400">No courses match.</p>
                          ) : (
                            visible.map((c) => {
                              const idStr = String(c.courseId);
                              const checked = selectedCourseIds.includes('all') || selectedCourseIds.includes(idStr);
                              return (
                                <label key={idStr} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedCourseIds((prev) => [...new Set([...prev.filter((x) => x !== 'all'), idStr])]);
                                      } else {
                                        setSelectedCourseIds((prev) => (prev.includes('all') ? visibleIds.filter((x) => x !== idStr) : prev.filter((x) => x !== idStr)));
                                      }
                                    }}
                                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                  />
                                  <span className="truncate text-slate-700">{courseLabel(c)}</span>
                                </label>
                              );
                            })
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="border-t border-slate-100 p-2">
                    <button
                      type="button"
                      onClick={() => setCourseDropdownOpen(false)}
                      className="w-full rounded-md bg-violet-600 py-1.5 text-sm font-semibold text-white hover:bg-violet-700"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">Category</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-violet-400">
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-violet-400" />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-violet-400" />
            </div>

            <div className="col-span-2 sm:col-span-3 lg:col-span-1">
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">Student (name / ID / reg. no.)</label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search student…"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-violet-400"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">Status</label>
              <div className="inline-flex w-full overflow-hidden rounded-lg border border-slate-200 sm:w-auto">
                {STATUS_FILTER_OPTIONS.map((opt, i) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatus(opt.value)}
                    className={`flex-1 whitespace-nowrap px-4 py-2 text-sm font-medium transition sm:flex-none ${i > 0 ? 'border-l border-slate-200' : ''} ${
                      status === opt.value ? opt.active : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={downloadExcel}
              disabled={rows.length === 0}
              className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
              Download Excel
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{courseName}</span>
            <span className="text-xs text-slate-400">{rows.length} ticket{rows.length === 1 ? '' : 's'}</span>
          </div>

          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
          ) : error ? (
            <div className="p-6 text-sm text-red-600">{error}</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">No tickets match these filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">Ticket ID</th>
                    <th className="px-3 py-2">Title</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Priority</th>
                    <th className="px-3 py-2">Course</th>
                    <th className="px-3 py-2">Batch</th>
                    <th className="px-3 py-2">Student</th>
                    <th className="px-3 py-2">Student ID</th>
                    <th className="px-3 py-2">Reg. No.</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Created At</th>
                    <th className="px-3 py-2">Resolved At</th>
                    <th className="px-3 py-2">Resolved By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.ticketId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-700">{r.ticketId}</td>
                      <td className="max-w-[220px] truncate px-3 py-2 text-slate-600">{r.title}</td>
                      <td className="px-3 py-2 text-slate-600">{STATUS_LABELS[r.status] || r.status}</td>
                      <td className="px-3 py-2 capitalize text-slate-600">{r.priority}</td>
                      <td className="px-3 py-2 text-slate-600">{r.courseName || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.batchName || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.studentName || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.studentId ?? '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.registrationNumber || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.categoryName || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{fmtDateTime(r.createdAt)}</td>
                      <td className="px-3 py-2 text-slate-600">{r.resolvedAt ? fmtDateTime(r.resolvedAt) : '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.resolvedByName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
